import { getDataGridToolbarProps } from 'features/shared/utility/dataGridToolbarHelper';
import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import FormSpinner from 'components/FormSpinner';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { CommonDataGrid } from 'features/shared/components/table';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSButton } from '../../../shared/components/buttons';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import PersonDnaSampleDialog from './PersonDnaSampleDialog';
import { getPersonDnaSamples, deleteDnaSample } from '../../services/userService';
import { DNA_SAMPLE_GRID_COLUMNS } from '../../constants/personDnaSampleFormConstants';

const PersonDnaSamplesTab = ({ personId }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [samples, setSamples] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingSample, setEditingSample] = useState(null);
  const peoplePerms = usePermissions(PERMISSION_RESOURCES.PERSON);
  const dnaSamplePerms = usePermissions(PERMISSION_RESOURCES.DNA_SAMPLE);
  const canAddDnaSample = Boolean(peoplePerms.Edit) && Boolean(dnaSamplePerms.Create);
  const canEditDnaSample = Boolean(peoplePerms.Edit) && Boolean(dnaSamplePerms.Edit);
  const canDeleteDnaSample = Boolean(peoplePerms.Edit) && Boolean(dnaSamplePerms.Delete);
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const loadSamples = useCallback(async () => {
    if (!personId) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getPersonDnaSamples(personId),
      onSuccess: (data) => setSamples(data),
    });
  }, [dispatch, personId]);

  const handleAdd = useCallback(() => {
    setEditingSample(null);
    setFormOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setEditingSample(row);
    setFormOpen(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
    setEditingSample(null);
  }, []);

  const handleSaved = useCallback(() => {
    handleFormClose();
    loadSamples();
  }, [handleFormClose, loadSamples]);

  const handleDelete = useCallback(
    (row) => {
      requestConfirm(
        `Are you sure you want to permanently delete this "${
          row.DNASampleID || 'DNA sample'
        }" record?`,
        async () => {
          await executeApiAction({
            setLoading,
            dispatch,
            action: () => deleteDnaSample(row.ptsDNASampleID),
            onSuccess: () => {
              dispatch(notify('DNA Sample deleted successfully', 'success'));
              loadSamples();
            },
          });
        },
        'Delete DNA Sample'
      );
    },
    [dispatch, requestConfirm, loadSamples]
  );

  useEffect(() => {
    loadSamples();
  }, [loadSamples]);

  return (
    <Box className="person-names-tab__grid-wrap">
      {loading && <FormSpinner />}
      <Box className="person-addresses-tab__toolbar">
        <RMSButton
          label="Add DNA Sample"
          prefixIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!canAddDnaSample}
        />
      </Box>

      <CommonDataGrid
        rows={samples}
        columns={DNA_SAMPLE_GRID_COLUMNS}
        getRowId={(row) => row.ptsDNASampleID}
        toolbar={getDataGridToolbarProps({ printTitle: 'DNA Samples' })}
        actions={{
          edit: { visible: true, onClick: handleEdit, disabled: !canEditDnaSample },
          delete: { visible: true, onClick: handleDelete, disabled: !canDeleteDnaSample },
        }}
      />

      {formOpen && (
        <PersonDnaSampleDialog
          open={formOpen}
          personId={personId}
          sample={editingSample}
          onClose={handleFormClose}
          onSaved={handleSaved}
        />
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </Box>
  );
};

export default PersonDnaSamplesTab;
